import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Inbox, CheckCheck } from "lucide-react";
import { StatusBadge, TypeBadge, SlaChip, DaysInStage, ActionDialog } from "../components/MrfBits";

export default function MrfInbox() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [dlg, setDlg] = useState(null); // {req, action}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/mrf/inbox").then(({ data }) => { setRows(data); setSelected({}); })
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const confirmAction = async (remarks, date) => {
    setBusy(true);
    try {
      await api.post(`/mrf/${dlg.req.req_id}/action`, { action: dlg.action, remarks, expected_review_on: date || null });
      toast.success(`${dlg.req.req_no} — ${dlg.action === "Approve" ? "approved" : dlg.action === "Reject" ? "rejected" : dlg.action === "Hold" ? "put on hold" : "returned for info"}`);
      setDlg(null);
      load();
      window.dispatchEvent(new Event("mrf-badge-refresh"));
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const bulkApprove = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mrf/bulk-approve", { req_ids: selectedIds, remarks: "Bulk approved" });
      if (data.approved.length) toast.success(`Approved ${data.approved.length}: ${data.approved.join(", ")}`);
      if (data.failed.length) toast.error(`${data.failed.length} failed — ${data.failed[0].reason}`);
      load();
      window.dispatchEvent(new Event("mrf-badge-refresh"));
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="mrf-inbox-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">MRF Approval Inbox</h1>
          <p className="text-sm text-[#6B7280] mt-1">Sorted by how long each requisition has been waiting on you.</p>
        </div>
        {selectedIds.length > 0 && (
          <button data-testid="bulk-approve-btn" disabled={busy} onClick={bulkApprove}
            className="flex items-center gap-2 bg-[#1B7F3B] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#166832] transition-colors duration-200 disabled:opacity-50 self-start">
            <CheckCheck size={15} /> Approve {selectedIds.length} selected
          </button>
        )}
      </div>

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : rows.length === 0 ? (
        <div data-testid="inbox-empty" className="bg-white border border-gray-200 rounded-sm p-12 text-center">
          <Inbox className="mx-auto text-gray-300" size={36} />
          <div className="mt-3 font-serif text-lg text-[#0E2240]">Nothing pending with you</div>
          <div className="text-sm text-[#6B7280] mt-1">Your accountability clock is clear.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="inbox-cards">
          {rows.map((r) => (
            <div key={r.req_id} data-testid={`inbox-card-${r.req_id}`}
              className={`bg-white border rounded-sm p-5 ${r.sla_status === "Breached" ? "border-[#BB162B]/50" : r.sla_status === "At Risk" ? "border-[#D97706]/50" : "border-gray-200"}`}>
              <div className="flex items-start justify-between gap-2">
                <label className="flex items-center gap-2">
                  <input data-testid={`inbox-select-${r.req_id}`} type="checkbox" checked={!!selected[r.req_id]}
                    onChange={(e) => setSelected((p) => ({ ...p, [r.req_id]: e.target.checked }))} />
                  <span className="text-xs font-semibold text-[#0E2240] tabular-nums">{r.req_no}</span>
                </label>
                <div className="flex items-center gap-2">
                  <DaysInStage days={r.days_in_current_stage} sla={r.sla_status} />
                  <SlaChip sla={r.sla_status} />
                </div>
              </div>
              <button onClick={() => navigate(`/requisitions/${r.req_id}`)} className="block text-left w-full">
                <div className="font-serif text-lg text-[#0E2240] mt-2 hover:underline">{r.positions_count} × {r.desig_name}</div>
                <div className="text-xs text-[#6B7280]">{r.branch_name} · {r.dept_name}</div>
              </button>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <TypeBadge type={r.requisition_type} forced={r.forced_beyond} />
                <span className={`text-[10px] uppercase tracking-wide ${r.priority === "Critical" ? "text-[#BB162B] font-semibold" : "text-[#6B7280]"}`}>{r.priority}</span>
                {r.delegate_of_name && <span className="text-[10px] text-[#BF9B30]">Delegate for {r.delegate_of_name}</span>}
              </div>
              <div className="mt-2 text-xs text-[#6B7280]">Raised by {r.raised_by_name || "—"} · {r.current_stage}</div>
              <p className="mt-2 text-xs text-[#0E2240] leading-snug line-clamp-2">{r.justification}</p>
              <div className="mt-4 grid grid-cols-4 gap-1.5 text-xs">
                <button data-testid={`inbox-approve-${r.req_id}`} onClick={() => setDlg({ req: r, action: "Approve" })}
                  className="py-1.5 bg-[#1B7F3B] text-white rounded-sm hover:bg-[#166832] transition-colors duration-200">Approve</button>
                <button data-testid={`inbox-reject-${r.req_id}`} onClick={() => setDlg({ req: r, action: "Reject" })}
                  className="py-1.5 border border-[#BB162B]/60 text-[#BB162B] rounded-sm hover:bg-[#BB162B]/5 transition-colors duration-200">Reject</button>
                <button data-testid={`inbox-return-${r.req_id}`} onClick={() => setDlg({ req: r, action: "Return for Info" })}
                  className="py-1.5 border border-gray-300 text-[#0E2240] rounded-sm hover:bg-gray-50 transition-colors duration-200">Return</button>
                <button data-testid={`inbox-hold-${r.req_id}`} onClick={() => setDlg({ req: r, action: "Hold" })}
                  className="py-1.5 border border-gray-300 text-[#6B7280] rounded-sm hover:bg-gray-50 transition-colors duration-200">Hold</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {dlg && (
        <ActionDialog open onOpenChange={(o) => !o && setDlg(null)} action={dlg.action}
          reqNo={`${dlg.req.req_no} — ${dlg.req.desig_name} at ${dlg.req.branch_name}`}
          onConfirm={confirmAction} busy={busy} />
      )}
    </div>
  );
}
